import React from 'react';
import i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import moment from 'moment';

import { withTheme } from '@material-ui/core/styles';

import { getFormat, twoDigits, makeGraph } from 'lib/commonLib';
import { dailyParGraph } from 'lib/api/time';

import CurrentStatus from 'components/atoms/CurrentStatus';
import OutlinedButton from 'components/atoms/OutlinedButton';
import ContentsWrap from 'components/molecules/ContentsWrap';
import ChartButtonWrap from 'components/molecules/ChartButtonWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const graphList = [
  {
    title: i18next.t('지출금액'),
    data: { bar: ['ADVRTS_AMT'] },
    yAxisData: {
      left: { ADVRTS_AMT: true },
      right: {},
    },
    referenceLine: {
      ADVRTS_AMT: {
        type: 'average',
        label: i18next.t('지출금액'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('노출'),
    data: { bar: ['PAR_EPRS_CNT'] },
    yAxisData: {
      left: { PAR_EPRS_CNT: true },
      right: {},
    },
    referenceLine: {
      PAR_EPRS_CNT: {
        type: 'average',
        label: i18next.t('노출'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: 'CTR',
    data: { bar: ['CLICK_RATE'] },
    yAxisData: {
      left: { CLICK_RATE: true, isPercent: true },
      right: {},
    },
    customizeData: {
      CLICK_RATE: {
        tooltipFormat: '0.000',
        isPercent: true,
      },
    },
    referenceLine: {
      CLICK_RATE: {
        type: 'average',
        label: i18next.t('클릭률'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: 'ECPM',
    data: { bar: ['ECPM'] },
    yAxisData: {
      left: { ECPM: true },
      right: {},
    },
    referenceLine: {
      ECPM: {
        type: 'average',
        label: 'ECPM',
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('세션ROAS'),
    data: { bar: ['SESSION_ROAS'] },
    yAxisData: {
      left: { SESSION_ROAS: true },
      right: {},
    },
    customizeData: {
      SESSION_ROAS: {
        isPercent: true,
      },
    },
    referenceLine: {
      SESSION_ROAS: {
        type: 'average',
        label: i18next.t('세션ROAS'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('쇼핑ROAS'),
    data: { bar: ['TRGT_SESSION_ROAS'] },
    yAxisData: {
      left: { TRGT_SESSION_ROAS: true, isPercent: true },
      right: {},
    },
    customizeData: {
      TRGT_SESSION_ROAS: {
        isPercent: true,
      },
    },
    referenceLine: {
      TRGT_SESSION_ROAS: {
        type: 'average',
        label: i18next.t('쇼핑ROAS'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('CW/SR점유율'),
    data: { line: ['SR_OCC_RATE', 'CW_OCC_RATE'] },
    yAxisData: {
      left: { SR_OCC_RATE: true, isPercent: true },
      right: { CW_OCC_RATE: true, isPercent: true },
    },
    customizeData: {
      SR_OCC_RATE: {
        tooltipFormat: '0.000',
        isPercent: true,
        visibleValue: true,
      },
      CW_OCC_RATE: {
        tooltipFormat: '0.000',
        isPercent: true,
        visibleValue: true,
      },
    },
  },
];

class StatisticsGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphData: [],
      infoMessage: '',
      loading: false,
      error: false,
    };
  }

  getData = () => {
    const { params, locale, t } = this.props;

    const { sDate, eDate } = params;
    const format = getFormat(locale);
    this.setState({
      ...this.state,
      loading: true,
      error: false,
      infoMessage: t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      }),
    });
    dailyParGraph({ ...params, uri: '/report/daily/par' })
      .then((response) => {
        const { data } = response.data;

        this.setState({
          ...this.state,
          primitiveData: data,
          loading: false,
          error: false,
        });

        this.handleChangeGraph(graphList[0], 0)();
      })
      .catch((error) => {
        this.setState({
          ...this.state,
          loading: false,
          error: true,
        });
      });
  };

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    const { params: prevParams } = prevProps;
    const { params } = this.props;

    if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
      this.getData();
    }
  }

  /**
   * 새로고침
   */
  handleRefesh = () => {
    this.getData();
  };

  // 월 일 or 일 반환
  makeLabel = (onlyDay) => (originalDate) => {
    const { t, theme } = this.props;
    const dateObj = moment(originalDate, 'YYYYMMDD');
    const month = twoDigits(dateObj.month() + 1);
    const date = dateObj.date();
    const day = dateObj.day();
    let color = '';

    if (day === 0) {
      color = theme.palette.common.red;
    } else if (day === 6) {
      color = theme.palette.common.blue;
    }

    return onlyDay ? (
      `${twoDigits(date)}${t('일')}`
    ) : (
      <span style={{ color }}>{`${t(month + '월')} ${twoDigits(date)}${t(
        '일'
      )}`}</span>
    );
  };

  xAxisTick = (tickProps) => {
    const { theme } = this.props;
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const dateObj = moment(value, 'YYYYMMDD');
    const day = dateObj.day();

    const dayStr = this.makeLabel(true)(value);
    let fill = '';
    //Sunday as 0 and Saturday as 6.
    if (day === 0) {
      fill = theme.palette.common.red;
    } else if (day === 6) {
      fill = theme.palette.common.blue;
    }

    return (
      <text x={x} y={y + 10} fill={fill} textAnchor="middle">
        {dayStr}
      </text>
    );
  };

  /**
   * 버튼 클릭하여 그래프 데이터 변경
   */
  handleChangeGraph =
    (
      {
        title,
        data,
        yAxisData,
        customizeData: customize,
        isPercent = false,
        referenceLine,
      },
      index
    ) =>
    (e) => {
      const { primitiveData } = this.state;

      if (typeof data !== 'undefined') {
        const {
          graphData,
          dataKeys,
          customizeData,
          keyLabel,
          stackId,
          referenceData,
        } = makeGraph({ data, customize, primitiveData, referenceLine });

        const result = (
          <MultiplexChart
            width="100%"
            xAxisTick={this.xAxisTick}
            makeLabel={this.makeLabel(false)}
            data={graphData}
            dataKeys={dataKeys}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={stackId.length !== 0 ? stackId.join('$') : null}
            yAxisData={yAxisData}
            legend
            title={title}
            key={title}
            referenceLine={referenceData}
            isPercent={isPercent}
          />
        );

        this.setState({
          ...this.state,
          curGraphComponent: result,
          selectedIdx: index,
        });
      }
    };

  render() {
    const { handleRefesh, handleChangeGraph } = this;
    const { t } = this.props;
    const {
      infoMessage,
      curGraphComponent,
      selectedIdx,
      primitiveData,
      loading,
      error,
    } = this.state;

    return (
      <SwitchingArea
        title={
          <>
            <InfoTitleArea
              title={t('일자별 데이터 그래프')}
              tooltipTitle={infoMessage}
              iconType="fainfoCircle"
              iconColor="graph"
            />
          </>
        }
        handleRefesh={handleRefesh}
      >
        {loading || error ? (
          <CurrentStatus type={loading ? 'loading' : error && 'error'} />
        ) : (
          <>
            <ChartButtonWrap>
              {graphList.map((item, index) => {
                return (
                  <OutlinedButton
                    key={item.title}
                    onClick={handleChangeGraph(item, index)}
                    selected={selectedIdx === index}
                    disabled={!Boolean(primitiveData)}
                  >
                    {item.title}
                  </OutlinedButton>
                );
              })}
            </ChartButtonWrap>
            <ContentsWrap contentsCount={1}>{curGraphComponent}</ContentsWrap>
          </>
        )}
      </SwitchingArea>
    );
  }
}

export default withRouter(
  withTranslation()(
    connect(
      ({ locale }) => ({
        locale: locale.locale,
      }),
      null
    )(withTheme(StatisticsGraph))
  )
);
